import { action, computed, observable } from 'mobx';
import { omit, pick } from 'lodash';
import { LayoutMode } from '@kentik/ui-shared/boards/layout';
import { showErrorToast, showSuccessToast } from 'core/components';
import { Model, ModelSaveOptions } from 'core/model/Model';
import api from 'core/util/api';
import generateLayout, { updateLayout } from 'app/stores/boards/generateLayout';
import BoardPanelCollection from 'app/stores/boards/BoardPanelCollection';
import BoardLoadScheduler from 'app/stores/boards/BoardLoadScheduler';
import $users from 'app/stores/user/$users';
import isEqualLoose from 'app/util/isEqualsLoose';
import $auth from 'app/stores/$auth';
import $app from 'app/stores/$app';
import $boards from 'app/stores/boards/$boards';
import $dictionary from 'app/stores/$dictionary';
import $labels from 'app/stores/label/$labels';
import $setup from 'app/stores/$setup';
import $recentlyViewed from 'app/stores/recentlyViewed/$recentlyViewed';
import { Icons } from 'app/stores/library/constants';
import { BOARD_LABEL, BoardData } from '@kentik/ui-shared/boards/boards';
import { RBACBoardPermission } from '@kentik/ui-shared/rbac/constants';
import { WithOptional } from '@kentik/ui-shared/types/util';
import { TimeRange } from '@kentik/ui-shared/query/query.types';
import BoardPanelModel, { BoardPanelDefaults, BoardPanelJson } from './BoardPanelModel';

export interface BoardJson extends WithOptional<BoardData, 'company_id' | 'user_id' | 'cdate' | 'edate'> {
  panels?: BoardPanelJson[];
  optimisticPanelIds?: string[];
}

class BoardModel extends Model<BoardJson> {
  panelCollection = new BoardPanelCollection([], { parent: this });

  loadScheduler = new BoardLoadScheduler({ shouldLoadAll: () => $app.isExport });

  isDuplicate = false;

  originalId?: string | number;

  // Stores original state for dirty checks
  @observable.ref
  lastSaved: WithOptional<BoardJson, 'id'> = {
    ...this.defaults,
    panels: []
  };

  // store the chart refs for the panels
  @observable.ref
  chartRefs = [];

  get urlRoot() {
    return '/api/ui/boards';
  }

  get defaults() {
    return {
      title: `Untitled ${BOARD_LABEL}`,
      type: null,
      draft: true,
      share_level: 'self' as const,
      config: {
        isDashboard: true,
        variables: []
      }
    };
  }

  get messages(): Record<'create' | 'update' | 'destroy' | 'duplicate', string> {
    const type = this.singlePanelView ? 'Chart' : this.typeLabel;
    return {
      create: `${type} "${this.name}" was added successfully`,
      update: `${type} "${this.name}" was updated successfully`,
      destroy: `${type} "${this.name}" was removed successfully`,
      duplicate: `${type} "${this.name}" was duplicated successfully`
    };
  }

  get type() {
    return 'Board';
  }

  get typeLabel() {
    return BOARD_LABEL;
  }

  get iconName() {
    return Icons.BOARD;
  }

  @computed
  get labels() {
    return $labels.getLabels('board', this.id);
  }

  @computed
  get labelIds() {
    return this.labels.map((label) => label.id);
  }

  @computed
  get selectOptionFormat() {
    return { value: String(this.id), label: this.name };
  }

  @computed
  get name() {
    return this.singlePanelView ? this.panelCollection.models[0].title : this.get('title');
  }

  get slugFavorite() {
    return `board$${this.id}`;
  }

  @computed
  get isFavorite() {
    return $setup.isFavorite(this.slugFavorite);
  }

  @computed
  get isTrending() {
    return $recentlyViewed.isTrending(this);
  }

  @computed
  get lastViewedDate() {
    return $recentlyViewed.getLastViewedDate(this);
  }

  @computed
  get singlePanelView() {
    return !this.get('config').isDashboard && this.panelCollection.size === 1 && this.panelCollection.hasUdePanel;
  }

  // prevent layout updates while dragging
  get isDraggingPanel() {
    return this.panelCollection.models.some((panel) => panel.isDragging);
  }

  get breakpoint() {
    return this.layoutMode === LayoutMode.LIST.id || $boards.isSmallScreen ? 'sm' : 'lg';
  }

  @computed
  get layoutMode() {
    const { layoutMode } = this.get('config');

    if (layoutMode && layoutMode in LayoutMode) {
      return layoutMode;
    }

    return LayoutMode.DEFAULT.id;
  }

  // layout is stored on each panel

  @computed
  get layout() {
    return generateLayout(this.panelCollection.topLevelPanels);
  }

  set layout(value) {
    const updatedPanels = updateLayout(this.panelCollection.topLevelPanels, value);
    this.bulkSavePanels(updatedPanels);
  }

  /**
   * This board or one of its panels has unsaved or draft changes
   */
  @computed
  get isDirty(): boolean {
    if (
      (!this.isNew || this.panelCollection.size > 0) &&
      (this.get('draft') || this.panelCollection.models.some((panel) => panel.isDirty || panel.isLayoutDirty))
    ) {
      return true;
    }

    const beforeData = this.lastSaved ?? { ...this.defaults, panels: [] };
    const afterData = this.serialize();

    const fields = ['title', 'config'];
    const beforeFields = { ...pick(beforeData, fields), panels: beforeData.panels?.map((panel) => panel.id) };
    const afterFields = { ...pick(afterData, fields), panels: afterData.panels?.map((panel) => panel.id) };

    return !isEqualLoose(beforeFields, afterFields);
  }

  @computed
  get userIsAuthor() {
    return Number(this.get('user_id')) === Number($auth.getActiveUserProperty('id', ''));
  }

  @computed
  get author(): string {
    const { userIsAuthor, isPreset } = this;
    if (isPreset) {
      return 'Kentik';
    }
    if (userIsAuthor) {
      return 'Me';
    }
    const userId = this.get('user_id');
    if (!userId) {
      return '';
    }
    const authorModel = $users.collection.modelById[userId];
    return authorModel ? (authorModel.get('user_full_name') as string) : '';
  }

  @computed
  get shareLevel() {
    const level = this.isPreset ? 'Preset' : 'Shared';
    return this.isUserLevel ? 'Private' : level;
  }

  @computed
  get isPreset() {
    return (
      this.get('share_level') === 'org' &&
      `${this.get('company_id')}` === `${$dictionary.dictionary.templateDashboardsCID}`
    );
  }

  @computed
  get isCompanyLevel() {
    return (
      this.get('share_level') === 'org' &&
      `${this.get('company_id')}` !== `${$dictionary.dictionary.templateDashboardsCID}`
    );
  }

  get isUserLevel() {
    return this.userIsAuthor && this.get('share_level') === 'self';
  }

  @computed
  get navigatePath() {
    return this.singlePanelView
      ? `/v4/library/boards/${this.id}/panel/${this.panelCollection.models[0].id}`
      : `/v4/library/boards/${this.id}`;
  }

  @computed
  private get userCanEdit() {
    return (
      !$app.isSubtenant &&
      (this.userIsAuthor || // Personal
        this.isCompanyLevel || // Company (gated by RBAC board::update/delete in canEdit/canRemove)
        (this.isPreset && $auth.isPresetCompany)) // Preset
    );
  }

  @computed
  get canEdit() {
    if (this.isNew) {
      return $auth.hasRbacPermissions([RBACBoardPermission.CREATE]);
    }

    return this.userCanEdit && $auth.hasRbacLabelPermissions([RBACBoardPermission.UPDATE], this.labels);
  }

  @computed
  get canRemove() {
    return this.userCanEdit && $auth.hasRbacLabelPermissions([RBACBoardPermission.DELETE], this.labels);
  }

  @computed
  get lastEditedDate() {
    return this.get('edate') || this.get('cdate');
  }

  getVariable(variableName: string) {
    return this.get('config').variables?.find((v) => v.name === variableName);
  }

  duplicate = (options: { save?: boolean; deserialize?: boolean } = {}) => {
    const { save = true, deserialize = true } = options;
    const attributes = omit(this.serialize(this.get()), ['id']);
    attributes.title = this.getUniqueCopyName('title');

    if (!this.collection) {
      throw new Error('Cannot duplicate a model that is not part of a collection');
    }

    const dupe = this.collection.build(attributes, { deserialize }) as BoardModel;
    dupe.isDuplicate = true;
    dupe.originalId = this.id;

    if (save) {
      dupe.save(attributes);
    }

    return dupe as this;
  };

  async addPanel({
    title = '',
    type,
    config = {},
    parent_panel_id = null,
    layout
  }: WithOptional<BoardPanelJson, BoardPanelDefaults>) {
    const panel = this.panelCollection.forge({ title, type, config, parent_panel_id, layout });
    this.panelCollection.add([panel]);
    const draft = type !== 'section'; // need to save sections in main table so sub-panels can reference it
    await panel.save({}, { draft, toast: false });
    return panel;
  }

  async removePanel(panel: BoardPanelModel) {
    const subPanels = this.panelCollection.getSubPanels(panel.id);

    // Delete sub-panels first; if any fail, abort before touching the parent
    if (subPanels.length > 0) {
      await Promise.all(subPanels.map((p) => p.draftDelete()));
    }

    await panel.draftDelete();
  }

  reset() {
    this.set(this.deserialize(this.lastSaved));
  }

  serialize(data?: BoardJson) {
    const optimisticPanelIds = this.panelCollection.map((panel) =>
      `${panel.id}`.startsWith('i_') ? `${panel.id}` : panel.optimisticId
    );

    return super.serialize({
      ...(data || this.get()),
      panels: this.panelCollection.serialize() as BoardPanelJson[],
      optimisticPanelIds
    }) as BoardJson;
  }

  @action
  deserialize(data: WithOptional<BoardJson, 'id'>) {
    const { panels } = data || this.defaults;

    // This has the effect of avoiding a bunch of useless setup when simply fetching the parent collection
    // We only want this to exist when we fetch the model
    if (panels) {
      if (panels.length === this.panelCollection.size) {
        // board save, just update existing panel models
        panels.forEach((panelData, idx) => {
          const panel = this.panelCollection.at(idx);
          if (panel) {
            panel.safeSet(panel.deserialize(panelData));
          }
        });

        // If this board went from optimisticId to real id, we need it fixed here.
        this.panelCollection.hashModelsById();
      } else {
        this.panelCollection.set(panels);
      }
    }

    this.lastSaved = data;

    return data;
  }

  @action
  save(attributes: Partial<BoardJson> = {}, options: ModelSaveOptions & { draft?: boolean } = {}) {
    const { draft = false } = options;
    this.set('draft', draft && (this.isNew || this.get('draft')));

    if (draft) {
      options.toast = false;
    }

    if (this.isDuplicate) {
      const url = `${this.urlRoot}/duplicate`;
      const label = 'creating';
      this.requestStatus = label;

      return api.post(url, { data: { id: this.originalId, title: this.get('title') } }).then(
        (attrs) => {
          showSuccessToast(`Successfully cloned ${this.typeLabel}.`);
          this.isDuplicate = false;
          this.requestStatus = null;
          this.set(this.deserialize(attrs));
          if (this.collection) {
            this.collection.add([this]);
          }
          return attrs;
        },
        (error) => {
          showErrorToast(`Failed to clone ${this.typeLabel}.`);
          this.error = { label, body: error };
          this.requestStatus = null;
          throw error;
        }
      );
    }

    return super.save(attributes, options).then((attrs) => $labels.labels.fetch({ force: true }).then(() => attrs));
  }

  setTimeRange = (time: TimeRange) => {
    const config = this.get('config');
    this.safeSet({ config: { ...config, time } });
    // Propagation to UDE panels is reactive: UdePanel reads board.config.time
    // and overlays it on the panel query via queryWithVariables (same pattern as board variables).
  };

  setTimezone = (timezone: string) => {
    const config = this.get('config');
    this.safeSet({ config: { ...config, timezone } });
  };

  setLiveUpdate = (updateFrequency: number) => {
    // off-screen panels only re-run when back in view; stop their old streams now
    this.panelCollection.models.forEach(({ queryModel }) => {
      if (queryModel && queryModel.query.updateFrequency !== updateFrequency) {
        queryModel.unsubscribe();
      }
    });

    const config = this.get('config');
    this.safeSet({ config: { ...config, updateFrequency } });
    // Propagation to UDE panels is reactive: UdePanel reads board.config.updateFrequency
    // and overlays it on the panel query via queryWithVariables (same pattern as board variables).
  };

  #bulkSavePanelIds: Set<number> = new Set();

  #bulkSavePanelsTimer?: NodeJS.Timeout;

  bulkSavePanels(panels: BoardPanelModel[]) {
    panels.forEach((panel) => {
      if (!panel.isNew) {
        this.#bulkSavePanelIds.add(panel.id as number);
      }
    });

    if (this.#bulkSavePanelIds.size === 0) {
      return;
    }

    if (this.isDraggingPanel) {
      // wait until dragging is done
      return;
    }

    clearTimeout(this.#bulkSavePanelsTimer);

    this.#bulkSavePanelsTimer = setTimeout(() => {
      const savePanels = Array.from(this.#bulkSavePanelIds)
        .map((id) => {
          const panel = this.panelCollection.get(id);
          const isDirty = panel?.isDirty || panel?.isLayoutDirty;
          // make sure panel still has updates
          return isDirty ? panel.serialize() : null;
        })
        .filter(Boolean);
      this.#bulkSavePanelIds.clear();

      if (savePanels.length > 0) {
        api
          .put(`${this.url}/panels`, {
            data: { panels: savePanels, draft: true },
            showErrorToast: false
          })
          .catch((err) => {
            console.error(`Unable to update panels: ${err}`);
          });
      }
    }, 300);
  }

  async discardDraftPanels() {
    this.setRequestStatus('destroying');
    const response = await api.put(`${this.url}/discardDraftPanels`).catch(() => null);
    this.setRequestStatus(null);

    if (response) {
      this.set(this.deserialize(response));
    }
  }

  @action
  refreshUdePanels() {
    this.panelCollection.models.forEach((panel) => {
      const { queryModel } = panel;
      if (!queryModel?.hasRun) {
        return;
      }

      const id = String(panel.id);
      if (this.loadScheduler.isRegistered(id)) {
        this.loadScheduler.schedule(id);
        return;
      }

      // unmounted (e.g. collapsed section): re-run when it mounts again
      queryModel.dirty = true;
    });
  }

  cleanupQueryModels() {
    this.loadScheduler.reset();
    this.panelCollection.models.forEach((panel) => {
      panel.queryModel?.cleanup();
    });
  }
}

export default BoardModel;
